/**
 * Shared provider/model filter for the Task Token Usage and Batching Efficiency
 * panels (2026-08-19 operator report: clicking "DeepSeek" in one panel left the
 * other still showing "All providers" — the two panels render the identical
 * PROVIDER_MODEL_FILTER_OPTIONS toggle but each kept its own independent
 * useState for it).
 *
 * A tiny useSyncExternalStore module rather than a React context: both panels
 * are mounted directly by App.tsx across three separate responsive layouts
 * (desktop/tablet/mobile tabs), and wrapping all three in a context provider
 * would touch far more of that file than this one filter needs. Module-scoped
 * state is fine here — one dashboard tab per browser tab, same as every other
 * top-level UI state in this app.
 */

import { useSyncExternalStore } from "react";

let filterIndex = 0;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): number {
  return filterIndex;
}

export function setSharedProviderFilterIndex(index: number): void {
  if (filterIndex === index) return;
  filterIndex = index;
  notify();
}

/** Index into PROVIDER_MODEL_FILTER_OPTIONS (TaskUsageWindows.tsx) — shared
 * across every consumer instead of per-component, so the two panels (and any
 * future one that adopts this same toggle) always agree on the selection. */
export function useSharedProviderFilterIndex(): [number, (index: number) => void] {
  const index = useSyncExternalStore(subscribe, getSnapshot);
  return [index, setSharedProviderFilterIndex];
}
